import React, { useEffect, useState } from 'react';
import {ChessPiece} from './ChessPieces';
import { RotateCcw, X, Bot, Users } from 'lucide-react';
import { useLang } from './i18n';
import { playGameCaptureSound, playGameMoveSound, playGameWinSound } from './sounds';

type GameType = 'chess' | 'tictactoe' | 'connect4';
type PlayMode = 'solo' | 'multi';

type Props = {
  roomId: string;
  me: any;
  socket?: any;
  onClose: () => void;
};

// --- CHESS ENGINE ---
type PieceColor = 'w' | 'b';
type PieceKind = 'p' | 'r' | 'n' | 'b' | 'q' | 'k';
type ChessPiece = { color: PieceColor; kind: PieceKind } | null;
type ChessBoard = ChessPiece[][];

const PIECE_UNICODE: Record<string, string> = {
  wk: '♔',
  wq: '♕',
  wr: '♖',
  wb: '♗',
  wn: '♘',
  wp: '♙',
  bk: '♚',
  bq: '♛',
  br: '♜',
  bb: '♝',
  bn: '♞',
  bp: '♟'
};

const PIECE_VALUES: Record<PieceKind, number> = { p: 10, n: 30, b: 32, r: 50, q: 90, k: 900 };

function createInitialChessBoard(): ChessBoard {
  const back: PieceKind[] = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'];
  const board: ChessBoard = Array.from({ length: 8 }, () => Array(8).fill(null));
  for (let c = 0; c < 8; c++) {
    board[0][c] = { color: 'b', kind: back[c] };
    board[1][c] = { color: 'b', kind: 'p' };
    board[6][c] = { color: 'w', kind: 'p' };
    board[7][c] = { color: 'w', kind: back[c] };
  }
  return board;
}

function cloneChessBoard(b: ChessBoard): ChessBoard {
  return b.map(row => row.map(cell => (cell ? { ...cell } : null)));
}

function inBounds(r: number, c: number) {
  return r >= 0 && r < 8 && c >= 0 && c < 8;
}

function getPseudoMoves(board: ChessBoard, r: number, c: number): [number, number][] {
  const p = board[r][c];
  if (!p) return [];
  const moves: [number, number][] = [];
  const dir = p.color === 'w' ? -1 : 1;

  if (p.kind === 'p') {
    if (inBounds(r + dir, c) && !board[r + dir][c]) {
      moves.push([r + dir, c]);
      const startRow = p.color === 'w' ? 6 : 1;
      if (r === startRow && !board[r + dir * 2][c]) {
        moves.push([r + dir * 2, c]);
      }
    }
    for (const dc of [-1, 1]) {
      const nr = r + dir;
      const nc = c + dc;
      if (inBounds(nr, nc) && board[nr][nc] && board[nr][nc]!.color !== p.color) {
        moves.push([nr, nc]);
      }
    }
  } else if (p.kind === 'n') {
    for (const [dr, dc] of [
      [-2, -1],
      [-2, 1],
      [-1, -2],
      [-1, 2],
      [1, -2],
      [1, 2],
      [2, -1],
      [2, 1]
    ]) {
      const nr = r + dr;
      const nc = c + dc;
      if (inBounds(nr, nc) && (!board[nr][nc] || board[nr][nc]!.color !== p.color)) {
        moves.push([nr, nc]);
      }
    }
  } else if (p.kind === 'k') {
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (!dr && !dc) continue;
        const nr = r + dr;
        const nc = c + dc;
        if (inBounds(nr, nc) && (!board[nr][nc] || board[nr][nc]!.color !== p.color)) {
          moves.push([nr, nc]);
        }
      }
    }
  } else {
    const dirs: [number, number][] = [];
    if (p.kind === 'r' || p.kind === 'q') dirs.push([-1, 0], [1, 0], [0, -1], [0, 1]);
    if (p.kind === 'b' || p.kind === 'q') dirs.push([-1, -1], [-1, 1], [1, -1], [1, 1]);
    for (const [dr, dc] of dirs) {
      let nr = r + dr;
      let nc = c + dc;
      while (inBounds(nr, nc)) {
        if (!board[nr][nc]) {
          moves.push([nr, nc]);
        } else {
          if (board[nr][nc]!.color !== p.color) moves.push([nr, nc]);
          break;
        }
        nr += dr;
        nc += dc;
      }
    }
  }
  return moves;
}

function isKingInCheck(board: ChessBoard, color: PieceColor): boolean {
  let kr = -1;
  let kc = -1;
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (board[r][c]?.color === color && board[r][c]?.kind === 'k') {
        kr = r;
        kc = c;
      }
    }
  }
  if (kr === -1) return true;
  const enemy: PieceColor = color === 'w' ? 'b' : 'w';
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (board[r][c]?.color === enemy) {
        const attacks = getPseudoMoves(board, r, c);
        if (attacks.some(([ar, ac]) => ar === kr && ac === kc)) return true;
      }
    }
  }
  return false;
}

function applyChessMove(board: ChessBoard, r1: number, c1: number, r2: number, c2: number): ChessBoard {
  const next = cloneChessBoard(board);
  const piece = next[r1][c1];
  next[r1][c1] = null;
  if (piece && piece.kind === 'p' && (r2 === 0 || r2 === 7)) {
    next[r2][c2] = { color: piece.color, kind: 'q' };
  } else {
    next[r2][c2] = piece;
  }
  return next;
}

function getLegalMoves(board: ChessBoard, r: number, c: number): [number, number][] {
  const p = board[r][c];
  if (!p) return [];
  return getPseudoMoves(board, r, c).filter(([nr, nc]) => {
    const sim = applyChessMove(board, r, c, nr, nc);
    return !isKingInCheck(sim, p.color);
  });
}

function getAllLegalMovesForColor(board: ChessBoard, color: PieceColor) {
  const all: { r1: number; c1: number; r2: number; c2: number }[] = [];
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (board[r][c]?.color === color) {
        for (const [r2, c2] of getLegalMoves(board, r, c)) {
          all.push({ r1: r, c1: c, r2, c2 });
        }
      }
    }
  }
  return all;
}

export function CallGames({ roomId, socket, onClose }: Props) {
  const [, , tr] = useLang();
  const [game, setGame] = useState<GameType>('chess');
  const [mode, setMode] = useState<PlayMode>('solo');

  // --- CHESS STATE ---
  const [chessBoard, setChessBoard] = useState<ChessBoard>(() => createInitialChessBoard());
  const [chessTurn, setChessTurn] = useState<PieceColor>('w');
  const [selectedSq, setSelectedSq] = useState<[number, number] | null>(null);
  const [lastMove, setLastMove] = useState<{ r1: number; c1: number; r2: number; c2: number } | null>(null);
  const [capturedW, setCapturedW] = useState<PieceKind[]>([]);
  const [capturedB, setCapturedB] = useState<PieceKind[]>([]);
  const [chessStatus, setChessStatus] = useState<string>('');

  // --- TIC-TAC-TOE STATE ---
  const [tttBoard, setTttBoard] = useState<(string | null)[]>(Array(9).fill(null));
  const [tttTurn, setTttTurn] = useState<'X' | 'O'>('X');
  const [tttScore, setTttScore] = useState({ x: 0, o: 0, ties: 0 });

  // --- CONNECT 4 STATE ---
  const [c4Board, setC4Board] = useState<(string | null)[][]>(() =>
    Array.from({ length: 6 }, () => Array(7).fill(null))
  );
  const [c4Turn, setC4Turn] = useState<'red' | 'yellow'>('red');
  const [c4Score, setC4Score] = useState({ red: 0, yellow: 0, ties: 0 });

  // Socket synchronization for 2-player mode
  useEffect(() => {
    if (!socket) return;
    const handler = (d: any) => {
      if (d.roomId !== roomId) return;
      if (d.game === 'chess') {
        if (d.action === 'move') {
          setChessBoard(d.payload.board);
          setChessTurn(d.payload.turn);
          setLastMove(d.payload.lastMove || null);
          setCapturedW(d.payload.capturedW || []);
          setCapturedB(d.payload.capturedB || []);
          setChessStatus(d.payload.status || '');
          setSelectedSq(null);
          playGameMoveSound();
        } else if (d.action === 'reset') {
          setChessBoard(createInitialChessBoard());
          setChessTurn('w');
          setSelectedSq(null);
          setLastMove(null);
          setCapturedW([]);
          setCapturedB([]);
          setChessStatus('');
        }
      } else if (d.game === 'tictactoe') {
        if (d.action === 'move') {
          setTttBoard(d.payload.board);
          setTttTurn(d.payload.turn);
          if (d.payload.score) setTttScore(d.payload.score);
          playGameMoveSound();
        } else if (d.action === 'reset') {
          setTttBoard(Array(9).fill(null));
          setTttTurn('X');
        }
      } else if (d.game === 'connect4') {
        if (d.action === 'move') {
          setC4Board(d.payload.board);
          setC4Turn(d.payload.turn);
          if (d.payload.score) setC4Score(d.payload.score);
          playGameMoveSound();
        } else if (d.action === 'reset') {
          setC4Board(Array.from({ length: 6 }, () => Array(7).fill(null)));
          setC4Turn('red');
        }
      }
    };
    socket.on('game:action', handler);
    return () => {
      socket.off('game:action', handler);
    };
  }, [socket, roomId]);

  // --- CHESS LOGIC & AI BOT ---
  function executeChessMove(r1: number, c1: number, r2: number, c2: number, currentBoard = chessBoard) {
    const target = currentBoard[r2][c2];
    const nextBoard = applyChessMove(currentBoard, r1, c1, r2, c2);
    const nextTurn: PieceColor = currentBoard[r1][c1]?.color === 'w' ? 'b' : 'w';
    const nextCapW = [...capturedW];
    const nextCapB = [...capturedB];
    if (target) {
      if (target.color === 'w') nextCapW.push(target.kind);
      else nextCapB.push(target.kind);
      playGameCaptureSound();
    } else {
      playGameMoveSound();
    }

    const legalNext = getAllLegalMovesForColor(nextBoard, nextTurn);
    const inCheck = isKingInCheck(nextBoard, nextTurn);
    let nextStatus = '';
    if (legalNext.length === 0) {
      if (inCheck) {
        nextStatus =
          nextTurn === 'w'
            ? tr('Checkmate! Black wins!', 'Schachmatt! Schwarz gewinnt!')
            : tr('Checkmate! White wins!', 'Schachmatt! Weiß gewinnt!');
        playGameWinSound();
      } else {
        nextStatus = tr('Stalemate! Draw!', 'Patt! Unentschieden!');
      }
    } else if (inCheck) {
      nextStatus = tr('Check!', 'Schach!');
    }

    const lm = { r1, c1, r2, c2 };
    setChessBoard(nextBoard);
    setChessTurn(nextTurn);
    setSelectedSq(null);
    setLastMove(lm);
    setCapturedW(nextCapW);
    setCapturedB(nextCapB);
    setChessStatus(nextStatus);

    if (mode === 'multi') {
      socket?.emit('game:action', {
        roomId,
        game: 'chess',
        action: 'move',
        payload: {
          board: nextBoard,
          turn: nextTurn,
          lastMove: lm,
          capturedW: nextCapW,
          capturedB: nextCapB,
          status: nextStatus
        }
      });
    }
  }

  // Solo AI Bot for Chess (plays Black 'b')
  useEffect(() => {
    if (game !== 'chess' || mode !== 'solo' || chessTurn !== 'b') return;
    const moves = getAllLegalMovesForColor(chessBoard, 'b');
    if (moves.length === 0) return;

    const timer = setTimeout(() => {
      // Score each move: captures, center control, check
      let best = moves[0];
      let bestScore = -9999;
      for (const m of moves) {
        let score = (Math.random() - 0.5) * 4;
        const captured = chessBoard[m.r2][m.c2];
        if (captured) score += PIECE_VALUES[captured.kind] * 1.2;
        if (m.r2 >= 2 && m.r2 <= 5 && m.c2 >= 2 && m.c2 <= 5) score += 4;
        const sim = applyChessMove(chessBoard, m.r1, m.c1, m.r2, m.c2);
        if (isKingInCheck(sim, 'w')) score += 12;
        if (score > bestScore) {
          bestScore = score;
          best = m;
        }
      }
      executeChessMove(best.r1, best.c1, best.r2, best.c2, chessBoard);
    }, 450);
    return () => clearTimeout(timer);
  }, [chessBoard, chessTurn, mode, game]);

  const legalForSelected = selectedSq ? getLegalMoves(chessBoard, selectedSq[0], selectedSq[1]) : [];

  function handleSquareClick(r: number, c: number) {
    if (chessStatus.includes('Checkmate') || chessStatus.includes('Schachmatt') || chessStatus.includes('Stalemate'))
      return;
    if (mode === 'solo' && chessTurn === 'b') return;

    if (selectedSq) {
      const [sr, sc] = selectedSq;
      if (sr === r && sc === c) {
        setSelectedSq(null);
        return;
      }
      if (legalForSelected.some(([lr, lc]) => lr === r && lc === c)) {
        executeChessMove(sr, sc, r, c);
        return;
      }
    }
    const piece = chessBoard[r][c];
    if (piece && piece.color === chessTurn) {
      setSelectedSq([r, c]);
    } else {
      setSelectedSq(null);
    }
  }

  function resetChess() {
    setChessBoard(createInitialChessBoard());
    setChessTurn('w');
    setSelectedSq(null);
    setLastMove(null);
    setCapturedW([]);
    setCapturedB([]);
    setChessStatus('');
    if (mode === 'multi') {
      socket?.emit('game:action', { roomId, game: 'chess', action: 'reset' });
    }
  }

  // --- TIC-TAC-TOE LOGIC & BOT ---
  function checkTttWinner(board: (string | null)[]) {
    const lines = [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8],
      [0, 3, 6],
      [1, 4, 7],
      [2, 5, 8],
      [0, 4, 8],
      [2, 4, 6]
    ];
    for (const [a, b, c] of lines) {
      if (board[a] && board[a] === board[b] && board[a] === board[c]) return board[a];
    }
    return board.every(Boolean) ? 'draw' : null;
  }

  const tttWinner = checkTttWinner(tttBoard);

  function handleTttClick(i: number, isBot = false) {
    if (tttBoard[i] || tttWinner) return;
    if (mode === 'solo' && tttTurn === 'O' && !isBot) return;
    const next = [...tttBoard];
    next[i] = tttTurn;
    const nextTurn = tttTurn === 'X' ? 'O' : 'X';
    const win = checkTttWinner(next);
    const nextScore = { ...tttScore };
    if (win === 'X') {
      nextScore.x++;
      playGameWinSound();
    } else if (win === 'O') {
      nextScore.o++;
      playGameWinSound();
    } else if (win === 'draw') {
      nextScore.ties++;
      playGameMoveSound();
    } else {
      playGameMoveSound();
    }
    setTttBoard(next);
    setTttTurn(nextTurn);
    setTttScore(nextScore);
    if (mode === 'multi') {
      socket?.emit('game:action', {
        roomId,
        game: 'tictactoe',
        action: 'move',
        payload: { board: next, turn: nextTurn, score: nextScore }
      });
    }
  }

  useEffect(() => {
    if (game !== 'tictactoe' || mode !== 'solo' || tttTurn !== 'O' || tttWinner) return;
    const timer = setTimeout(() => {
      const empty = tttBoard.map((v, idx) => (v === null ? idx : -1)).filter(idx => idx !== -1);
      if (!empty.length) return;
      // Try winning or blocking X
      let pick = empty[Math.floor(Math.random() * empty.length)];
      for (const mark of ['O', 'X']) {
        for (const idx of empty) {
          const test = [...tttBoard];
          test[idx] = mark;
          if (checkTttWinner(test) === mark) {
            pick = idx;
            break;
          }
        }
      }
      if (tttBoard[4] === null && Math.random() > 0.25) pick = 4;
      handleTttClick(pick, true);
    }, 380);
    return () => clearTimeout(timer);
  }, [tttBoard, tttTurn, mode, game, tttWinner]);

  function resetTtt() {
    setTttBoard(Array(9).fill(null));
    setTttTurn('X');
    if (mode === 'multi') socket?.emit('game:action', { roomId, game: 'tictactoe', action: 'reset' });
  }

  // --- CONNECT 4 LOGIC & BOT ---
  function checkC4Winner(board: (string | null)[][]) {
    for (let r = 0; r < 6; r++) {
      for (let c = 0; c < 4; c++) {
        const color = board[r][c];
        if (color && color === board[r][c + 1] && color === board[r][c + 2] && color === board[r][c + 3]) return color;
      }
    }
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 7; c++) {
        const color = board[r][c];
        if (color && color === board[r + 1][c] && color === board[r + 2][c] && color === board[r + 3][c]) return color;
      }
    }
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 4; c++) {
        const color = board[r][c];
        if (
          color &&
          color === board[r + 1][c + 1] &&
          color === board[r + 2][c + 2] &&
          color === board[r + 3][c + 3]
        )
          return color;
      }
    }
    for (let r = 0; r < 3; r++) {
      for (let c = 3; c < 7; c++) {
        const color = board[r][c];
        if (
          color &&
          color === board[r + 1][c - 1] &&
          color === board[r + 2][c - 2] &&
          color === board[r + 3][c - 3]
        )
          return color;
      }
    }
    if (board.every(row => row.every(Boolean))) return 'draw';
    return null;
  }

  const c4Winner = checkC4Winner(c4Board);

  function handleC4Drop(col: number, isBot = false) {
    if (c4Winner) return;
    if (mode === 'solo' && c4Turn === 'yellow' && !isBot) return;
    let targetRow = -1;
    for (let r = 5; r >= 0; r--) {
      if (!c4Board[r][col]) {
        targetRow = r;
        break;
      }
    }
    if (targetRow === -1) return;
    const next = c4Board.map(row => [...row]);
    next[targetRow][col] = c4Turn;
    const nextTurn = c4Turn === 'red' ? 'yellow' : 'red';
    const win = checkC4Winner(next);
    const nextScore = { ...c4Score };
    if (win === 'red') {
      nextScore.red++;
      playGameWinSound();
    } else if (win === 'yellow') {
      nextScore.yellow++;
      playGameWinSound();
    } else if (win === 'draw') {
      nextScore.ties++;
      playGameMoveSound();
    } else {
      playGameMoveSound();
    }
    setC4Board(next);
    setC4Turn(nextTurn);
    setC4Score(nextScore);
    if (mode === 'multi') {
      socket?.emit('game:action', {
        roomId,
        game: 'connect4',
        action: 'move',
        payload: { board: next, turn: nextTurn, score: nextScore }
      });
    }
  }

  useEffect(() => {
    if (game !== 'connect4' || mode !== 'solo' || c4Turn !== 'yellow' || c4Winner) return;
    const timer = setTimeout(() => {
      const validCols = [0, 1, 2, 3, 4, 5, 6].filter(c => !c4Board[0][c]);
      if (!validCols.length) return;
      const pick = validCols.includes(3) && Math.random() > 0.45 ? 3 : validCols[Math.floor(Math.random() * validCols.length)];
      handleC4Drop(pick, true);
    }, 420);
    return () => clearTimeout(timer);
  }, [c4Board, c4Turn, mode, game, c4Winner]);

  function resetC4() {
    const fresh = Array.from({ length: 6 }, () => Array(7).fill(null));
    setC4Board(fresh);
    setC4Turn('red');
    if (mode === 'multi') socket?.emit('game:action', { roomId, game: 'connect4', action: 'reset' });
  }

  const tttLine = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]].find(
    ([a, b, c]) => tttBoard[a] && tttBoard[a] === tttBoard[b] && tttBoard[a] === tttBoard[c]
  );

  return (
    <section className="call-games-panel" aria-label={tr('Call Games', 'Minispiele im Anruf')}>
      <header className="games-header">
        <div className="game-tabs">
          <button
            type="button"
            className={game === 'chess' ? 'active' : ''}
            onClick={() => setGame('chess')}
          >
            ♟️ {tr('Chess', 'Schach')}
          </button>
          <button
            type="button"
            className={game === 'tictactoe' ? 'active' : ''}
            onClick={() => setGame('tictactoe')}
          >
            {tr('Tic-Tac-Toe', 'Drei Gewinnt')}
          </button>
          <button
            type="button"
            className={game === 'connect4' ? 'active' : ''}
            onClick={() => setGame('connect4')}
          >
            {tr('Connect 4', 'Vier Gewinnt')}
          </button>
        </div>
        <button type="button" className="close-games" onClick={onClose} title={tr('Close games', 'Schließen')}>
          <X size={18} />
        </button>
      </header>

      <div className="game-mode-switcher">
        <button
          type="button"
          className={'game-mode-pill ' + (mode === 'solo' ? 'active' : '')}
          onClick={() => setMode('solo')}
        >
          <Bot size={14} /> {tr('Solo vs. Bot', 'Alleine (vs. Bot)')}
        </button>
        <button
          type="button"
          className={'game-mode-pill ' + (mode === 'multi' ? 'active' : '')}
          onClick={() => setMode('multi')}
        >
          <Users size={14} /> {tr('2 Players (Call Sync)', 'Zu zweit (Im Anruf)')}
        </button>
      </div>

      {game === 'chess' && (
        <div className="chess-view">
          <div className="game-status-bar">
            <span className="status-label">
              {chessStatus && (chessStatus.includes('Checkmate') || chessStatus.includes('Schachmatt') || chessStatus.includes('Stalemate')) ? (
                <b style={{ color: '#f59e0b' }}>{chessStatus}</b>
              ) : (
                <span>
                  {tr('Turn:', 'Am Zug:')}{' '}
                  <b style={{ color: chessTurn === 'w' ? '#ffffff' : '#94a3b8' }}>
                    {chessTurn === 'w' ? tr('White (♔)', 'Weiß (♔)') : tr('Black (♚)', 'Schwarz (♚)')}
                  </b>
                  {chessStatus && <span className="chess-check-badge">{chessStatus}</span>}
                </span>
              )}
            </span>
            <button type="button" className="reset-btn" onClick={resetChess}>
              <RotateCcw size={13} /> {tr('New Game', 'Neu')}
            </button>
          </div>

          <div className="chess-captured-row">
            <span>{capturedB.map((k, i) => <i key={i}><ChessPiece color="b" kind={k} size={18} /></i>)}</span>
            <span>{capturedW.map((k, i) => <i key={i}><ChessPiece color="w" kind={k} size={18} /></i>)}</span>
          </div>

          <div className="chess-board-frame">
            <div className="chess-board">
              {chessBoard.map((row, r) =>
                row.map((cell, c) => {
                  const isLight = (r + c) % 2 === 0;
                  const isSelected = selectedSq?.[0] === r && selectedSq?.[1] === c;
                  const isLegal = legalForSelected.some(([lr, lc]) => lr === r && lc === c);
                  const isLast =
                    (lastMove?.r1 === r && lastMove?.c1 === c) || (lastMove?.r2 === r && lastMove?.c2 === c);
                  return (
                    <button
                      key={`${r}-${c}`}
                      type="button"
                      className={
                        'chess-sq ' +
                        (isLight ? 'sq-light ' : 'sq-dark ') +
                        (isSelected ? 'sq-selected ' : '') +
                        (isLast ? 'sq-last ' : '') +
                        (isLegal ? (cell ? 'sq-legal-capture' : 'sq-legal-dot') : '')
                      }
                      onClick={() => handleSquareClick(r, c)}
                    >
                      {c === 0 && <small className="sq-coord-rank">{8 - r}</small>}
                      {r === 7 && <small className="sq-coord-file">{'abcdefgh'[c]}</small>}
                      {cell && (
                        <span key={cell.color + cell.kind + (isLast ? '-moved' : '')} className={'chess-piece piece-' + cell.color + (isLast ? ' just-moved' : '')}>
                          <ChessPiece color={cell.color} kind={cell.kind} />
                        </span>
                      )}
                      {isLegal && !cell && <i className="legal-move-indicator" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {game === 'tictactoe' && (
        <div className="ttt-view">
          <div className="game-status-bar">
            <span className="status-label">
              {tttWinner === 'draw' ? (
                tr('Draw!', 'Unentschieden!')
              ) : tttWinner ? (
                `${tr('Winner:', 'Gewinner:')} ${tttWinner}!`
              ) : (
                <span>
                  {tr('Turn:', 'Am Zug:')} <b className={'mark-' + tttTurn}>{tttTurn}</b>
                </span>
              )}
            </span>
            <button type="button" className="reset-btn" onClick={resetTtt}>
              <RotateCcw size={13} /> {tr('New', 'Neu')}
            </button>
          </div>
          <div className="ttt-grid">
            {tttBoard.map((val, idx) => (
              <button
                type="button"
                key={idx}
                className={'ttt-cell ' + (val ? 'filled mark-' + val : '') + (tttLine?.includes(idx) ? ' is-winning' : '')}
                onClick={() => handleTttClick(idx)}
                disabled={!!val || !!tttWinner}
                aria-label={val || tr('Empty', 'Leer')}
              >
                {val === 'X' && (
                  <svg key="x" viewBox="0 0 100 100" className="ttt-mark ttt-x"><path d="M24 24 76 76" /><path d="M76 24 24 76" /></svg>
                )}
                {val === 'O' && (
                  <svg key="o" viewBox="0 0 100 100" className="ttt-mark ttt-o"><circle cx="50" cy="50" r="27" /></svg>
                )}
              </button>
            ))}
          </div>
          <div className="game-score">
            <span>X: {tttScore.x}</span>
            <span>{tr('Ties:', 'Unentschieden:')} {tttScore.ties}</span>
            <span>O: {tttScore.o}</span>
          </div>
        </div>
      )}

      {game === 'connect4' && (
        <div className="c4-view">
          <div className="game-status-bar">
            <span className="status-label">
              {c4Winner === 'draw' ? (
                tr('Draw!', 'Unentschieden!')
              ) : c4Winner ? (
                `${tr('Winner:', 'Gewinner:')} ${c4Winner === 'red' ? tr('Red', 'Rot') : tr('Yellow', 'Gelb')}!`
              ) : (
                <span>
                  {tr('Turn:', 'Am Zug:')} <i className={'c4-dot-sm c4-token-' + c4Turn} />
                  {c4Turn === 'red' ? tr('Red', 'Rot') : tr('Yellow', 'Gelb')}
                </span>
              )}
            </span>
            <button type="button" className="reset-btn" onClick={resetC4}>
              <RotateCcw size={13} /> {tr('New', 'Neu')}
            </button>
          </div>
          <div className="c4-board">
            {Array.from({ length: 7 }, (_, col) => (
              <button
                type="button"
                key={col}
                className="c4-col"
                onClick={() => handleC4Drop(col)}
                disabled={!!c4Winner || !!c4Board[0][col]}
                title={tr('Column ', 'Spalte ') + (col + 1)}
              >
                {Array.from({ length: 6 }, (_, row) => (
                  <div key={row} className="c4-slot">
                    <span
                      key={c4Board[row][col] || 'empty'}
                      className={'c4-token ' + (c4Board[row][col] ? 'c4-token-' + c4Board[row][col] : '')}
                      style={{ '--drop-rows': row + 1 } as React.CSSProperties}
                    />
                  </div>
                ))}
              </button>
            ))}
          </div>
          <div className="game-score">
            <span style={{ color: '#ff5566' }}>
              {tr('Red:', 'Rot:')} {c4Score.red}
            </span>
            <span>
              {tr('Ties:', 'Unentschieden:')} {c4Score.ties}
            </span>
            <span style={{ color: '#f1c40f' }}>
              {tr('Yellow:', 'Gelb:')} {c4Score.yellow}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
